// TROIA-V3-FRONTEND/src/modules/crm/ranking/components/RankingScopeSelector.tsx
import React, { useMemo } from 'react';
import type { GamificationRankingScope } from '@troia-v3/types';

import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { ContextSelector } from '@/shared/components/ui';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';

/** Item do seletor: `users` | `teams` | `units` | `users_by_team:{id}` | `users_by_unit:{id}`. */
export interface ScopeSelection { scope: GamificationRankingScope; teamId?: string; unitId?: string; label: string }

/** Uma linha do seletor de recorte. */
export interface ScopeItem { id: string; name: string }

export function parseScopeItem(id: string, label: string): ScopeSelection {
  if (id.startsWith('users_by_team:')) return { scope: 'users_by_team', teamId: id.slice('users_by_team:'.length), label };
  if (id.startsWith('users_by_unit:')) return { scope: 'users_by_unit', unitId: id.slice('users_by_unit:'.length), label };
  return { scope: id as GamificationRankingScope, label };
}

export const scopeItemId = (s: ScopeSelection): string =>
  s.scope === 'users_by_team' ? `users_by_team:${s.teamId ?? ''}` : s.scope === 'users_by_unit' ? `users_by_unit:${s.unitId ?? ''}` : s.scope;

/** Recortes que só existem colados num id (equipe/unidade) — precisam do dropdown para virar item. */
export const scopeNeedsId = (scope: GamificationRankingScope): boolean => scope === 'users_by_team' || scope === 'users_by_unit';

/**
 * Monta as linhas do seletor a partir dos recortes ligados pelo admin. Fica
 * fora do componente porque a PÁGINA também precisa da lista: é nela que o
 * recorte restaurado do `localStorage` (`users_by_team:{id}`) reencontra o
 * nome da equipe/unidade — e descobre que o id sumiu (item G da onda final).
 * As duas chamadas compartilham o mesmo cache do React Query, então não há
 * requisição a mais.
 */
export function useScopeItems(rankings: GamificationRankingScope[]): { items: ScopeItem[]; isLoading: boolean } {
  const teamsEnabled = rankings.includes('users_by_team');
  const unitsEnabled = rankings.includes('users_by_unit');
  const { data: teams = [], isSuccess: teamsReady } = useTeamsDropdown({ enabled: teamsEnabled });
  const { data: units = [], isSuccess: unitsReady } = useUnitsDropdown(unitsEnabled);

  const items = useMemo(() => {
    const list: ScopeItem[] = [];
    if (rankings.includes('users')) list.push({ id: 'users', name: 'Vendedores da empresa' });
    if (rankings.includes('users_by_unit')) units.forEach((u) => list.push({ id: `users_by_unit:${u.value}`, name: `Vendedores · ${u.label}` }));
    if (rankings.includes('users_by_team')) teams.forEach((t) => list.push({ id: `users_by_team:${t.id}`, name: `Vendedores · ${t.name}` }));
    if (rankings.includes('teams')) list.push({ id: 'teams', name: 'Ranking de equipes' });
    if (rankings.includes('units')) list.push({ id: 'units', name: 'Ranking de unidades' });
    return list;
  }, [rankings, teams, units]);

  // "Ainda carregando" = existe um dropdown LIGADO que ainda não respondeu com
  // sucesso. Usa `isSuccess` (e não `isLoading`) de propósito: um `useQuery`
  // com `enabled: false` reporta `isLoading: false`, e quem espera a lista pra
  // decidir se um id salvo "sumiu" concluiria "sumiu" com a lista vazia do
  // estado desligado (item G da onda final).
  return { items, isLoading: (teamsEnabled && !teamsReady) || (unitsEnabled && !unitsReady) };
}

interface Props { items: ScopeItem[]; selectedId: string; onSelect: (s: ScopeSelection) => void }

/** Seletor de recorte do header (`.scope-sel` do mockup, via `ContextSelector` — mesmo primitivo do `.funnel`). */
export const RankingScopeSelector: React.FC<Props> = ({ items, selectedId, onSelect }) => (
  <ContextSelector
    items={items}
    selectedId={selectedId}
    onSelect={(id) => { const it = items.find((i) => i.id === id); if (it) onSelect(parseScopeItem(it.id, it.name)); }}
    placeholder="Selecionar recorte"
    data-testid="ranking-scope"
  />
);
RankingScopeSelector.displayName = 'RankingScopeSelector';
